/**
 * Directory (region resolver) client.
 *
 * The Directory Service maps a user identity (uid/email) to its home cluster's
 * API base URL. We call it once, pre-login, from the entered email so the
 * dashboard's profile fetch + data load hit the correct regional cluster —
 * without relying on Firebase (auth is Firebase-first here, but routing must not
 * depend on it). See reflective-learning-api/docs/australia-cluster-migration-plan.md (§9).
 *
 * It NEVER blocks or breaks login: any failure (directory unconfigured, network
 * error, timeout, unknown user) is swallowed and the app keeps its default API.
 *
 * NOTE: internal admins exist in every cluster, so this resolves them to a
 * regional cluster via the tie-break; userStore.login() resets them to the
 * default unless they've explicitly chosen a region (see hasExplicitRegion).
 */

import { setRegionApiUrl, apiUrlForRegion } from "@/lib/runtime-url-resolver";

const directoryUrl = stripTrailingSlash(process.env.VUE_APP_DIRECTORY_URL || "");
const RESOLVE_TIMEOUT_MS = 4000;

function stripTrailingSlash(url: string): string {
  return url.replace(/\/+$/, "");
}

interface RegionResult {
  region: string;
  apiUrl: string;
}

/**
 * Ask the Directory Service which region an identity belongs to.
 * Returns null when unconfigured (e.g. local dev) or on any failure — the caller
 * then falls back to the default API URL.
 */
export async function resolveRegion(identity: {
  uid?: string;
  email?: string;
}): Promise<RegionResult | null> {
  if (!directoryUrl) return null;

  const body = identity.uid
    ? { uid: identity.uid }
    : identity.email
      ? { email: identity.email.trim().toLowerCase() }
      : null;
  if (!body) return null;

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), RESOLVE_TIMEOUT_MS);
    // POST (not GET) so the identity travels in the body, never in the URL.
    const response = await fetch(`${directoryUrl}/resolve`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      signal: controller.signal,
      mode: "cors",
    });
    clearTimeout(timeoutId);

    if (!response.ok) return null;
    const data = await response.json();
    // The dashboard keys off the region CODE (it maps that to its OWN
    // dashboard-api), not the directory's apiUrl (which is the learner api).
    if (data && typeof data.region === "string" && data.region) {
      return {
        region: data.region,
        apiUrl: typeof data.apiUrl === "string" ? data.apiUrl : "",
      };
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Resolve the region for a login attempt and point the app's API at it.
 * Safe to `await` unconditionally at the top of the login flow — a no-op when the
 * directory is unconfigured or the identity can't be resolved. Passes the region
 * as non-explicit so the internal-admin default reset in userStore.login applies.
 */
export async function applyRegionForLogin(identity: {
  uid?: string;
  email?: string;
}): Promise<void> {
  const result = await resolveRegion(identity);
  if (!result) return;
  // Map the resolved region CODE to THIS app's regional dashboard-api. The
  // directory's apiUrl is the learner api and must not be used here. An empty
  // result (legacy/default region, or an unmapped code) leaves the default in
  // place so we never point the dashboard at the wrong backend.
  const regionalApiUrl = apiUrlForRegion(result.region);
  if (regionalApiUrl) {
    setRegionApiUrl(regionalApiUrl);
  }
}
